<template>
  <div>
    <h2 class="heading-2 mb-24 pl-24">{{ i18n(translations.title) }}</h2>
    <RevList>
      <RevListItemInteractive
        v-for="(step, index) in filteredSteps"
        :key="step.code"
        class="p-0"
        :to="{ name: getRouteNameForStep(step.code, index) }"
      >
        <template v-if="ICONS[step.status]" #prefix>
          <component
            :is="ICONS[step.status]?.component"
            :color="ICONS[step.status]?.color"
          />
        </template>
        <template #label>
          <span class="body-1">{{ step.label }}</span>
        </template>
        <template #suffix>
          <IconChevronRight />
        </template>
      </RevListItemInteractive>
      <RevButton
        class="mt-24 w-full"
        :disabled="!allStepsDone"
        :to="{ name: ROUTE_NAMES.KYB.STEPS.REVIEW }"
        variant="primary"
      >
        {{ i18n(translations.reviewStepLabel) }}
      </RevButton>
    </RevList>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevList } from '@ds/components/List'
import { RevListItemInteractive } from '@ds/components/ListItemInteractive'
import { IconCheckInCircleFilled } from '@ds/icons/IconCheckInCircleFilled'
import { IconChevronRight } from '@ds/icons/IconChevronRight'
import { IconCrossInCircle } from '@ds/icons/IconCrossInCircle'

import { ROUTE_NAMES } from '~/constants/routes'

import type { KybStep } from '../../Index.types'

import { KYB_STEP_CODES, KYB_STEP_STATUS } from './Stepper.constants'
import translations from './Stepper.translations'

const props = defineProps<{
  steps: KybStep[]
}>()

const ICONS = {
  [KYB_STEP_STATUS.TODO]: null,
  [KYB_STEP_STATUS.DONE]: {
    component: IconCheckInCircleFilled,
    color: 'green',
  },
  [KYB_STEP_STATUS.ERROR]: {
    component: IconCrossInCircle,
    color: 'red',
  },
}

const i18n = useI18n()

const filteredSteps = computed<KybStep[]>(() => {
  const steps: KybStep[] = []
  for (const stepCode of KYB_STEP_CODES) {
    const stepToFilter = props.steps.find((step) => step.code === stepCode)
    if (stepToFilter) {
      steps.push(stepToFilter)
    }
  }

  return steps
})

const allStepsDone = computed(() => {
  return filteredSteps.value.every(
    (step) => step.status === KYB_STEP_STATUS.DONE,
  )
})

const getRouteNameForStep = (stepCode: string, index: number): string => {
  return ROUTE_NAMES.KYB.STEPS[
    `STEP_${index + 1}` as keyof typeof ROUTE_NAMES.KYB.STEPS
  ]
}
</script>
